import React, { useState, useEffect, useMemo } from 'react';
import { useRafraichissementAuto } from '../rafraichissementAuto';
import { useBlocageDefilement } from '../blocageDefilement';
import PanneauParcours from './SuiviParcours';
import { CreditCard, RefreshCw, CheckCircle, XCircle, Clock, Users, Coins, Search, X,
         AlertTriangle, Activity, ChevronRight } from 'lucide-react';

/* ---------------------------------------------------------------------------
   Onglet "Clients Monetico" : vue globale des paiements + fiche detaillee.
   Lecture seule. S'appuie sur /api/monetico/synthese et, pour le detail,
   sur /api/monetico/paiement/:id/historique (qui resout la reference connue de
   Monetico : pour une mensualite, c'est celle de l'abonnement).
   --------------------------------------------------------------------------- */
const API = process.env.REACT_APP_API_URL || '/api';
const jetonEntete = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const euros = (v) => (Number(v) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
/* Les dates sont stockees en UTC : on les affiche systematiquement en heure
   francaise, changement d'heure compris. */
const PARIS = 'Europe/Paris';
const dateFr = (v) => (v ? new Date(v).toLocaleDateString('fr-FR',
  { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: PARIS }) : '—');
const heureFr = (v) => (v ? new Date(v).toLocaleTimeString('fr-FR',
  { hour: '2-digit', minute: '2-digit', timeZone: PARIS }) : '');
const dateHeure = (v) => (v ? `${dateFr(v)} à ${heureFr(v)}` : '—');

/* "il y a 3 minutes" : dit a quel point l'information affichee est fraiche. */
const ilYA = (v) => {
  if (!v) return null;
  const secondes = Math.max(0, Math.round((Date.now() - new Date(v).getTime()) / 1000));
  if (secondes < 60) return "à l’instant";
  const min = Math.round(secondes / 60);
  if (min < 60) return `il y a ${min} minute${min > 1 ? 's' : ''}`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} heure${h > 1 ? 's' : ''}`;
  return `le ${dateFr(v)} à ${heureFr(v)}`;
};

/* Bornes d'une periode, calculees sur les journees en heure de Paris puis
   converties en UTC pour interroger la base. */
function bornesPeriode(cle) {
  const maintenant = new Date();
  const jourParis = (d) => new Date(d.toLocaleString('en-US', { timeZone: PARIS }));
  const debutJour = (d) => { const x = jourParis(d); x.setHours(0, 0, 0, 0); return x; };
  const finJour = (d) => { const x = jourParis(d); x.setHours(23, 59, 59, 999); return x; };
  const decalage = () => jourParis(maintenant).getTime() - maintenant.getTime();
  const versUtc = (d) => new Date(d.getTime() - decalage()).toISOString().slice(0, 19) + 'Z';

  const auj = debutJour(maintenant);
  switch (cle) {
    case 'aujourdhui': return [versUtc(auj), versUtc(finJour(maintenant))];
    case 'semaine': { const d = new Date(auj); d.setDate(d.getDate() - 6);
                      return [versUtc(d), versUtc(finJour(maintenant))]; }
    case 'mois': { const d = new Date(auj); d.setDate(1);
                   return [versUtc(d), versUtc(finJour(maintenant))]; }
    case 'mois_precedent': {
      const debut = new Date(auj); debut.setDate(1); debut.setMonth(debut.getMonth() - 1);
      const fin = new Date(auj); fin.setDate(0); fin.setHours(23, 59, 59, 999);
      return [versUtc(debut), versUtc(fin)]; }
    case 'annee': { const d = new Date(auj); d.setMonth(0, 1);
                    return [versUtc(d), versUtc(finJour(maintenant))]; }
    default: return [null, null];
  }
}

const COULEURS = {
  PAYE:    { fond: '#e8f5e9', texte: '#1b5e20', icone: CheckCircle },
  REFUSE:  { fond: '#ffebee', texte: '#b71c1c', icone: XCircle },
  PENDING: { fond: '#fff8e1', texte: '#8d6e00', icone: Clock },
  NON_ENCAISSE: { fond: '#fff3e0', texte: '#e65100', icone: AlertTriangle },
  JAMAIS: { fond: '#eceff1', texte: '#b71c1c', icone: XCircle },
  ABANDON: { fond: '#f5f5f5', texte: '#78909c', icone: XCircle },
  TECHNIQUE: { fond: '#f3e8ff', texte: '#6b21a8', icone: AlertTriangle },
  INCONNU: { fond: '#eceff1', texte: '#455a64', icone: Clock }
};

// L'etiquette ne dit JAMAIS "PAYE" sans encaissement constate : un paiement
// autorise mais jamais recouvre n'a fait entrer aucun euro sur le compte.
function Etiquette({ statut, ligne }) {
  const encaisse = ligne ? Number(ligne.encaisse_reel) === 1 : null;
  const test = ligne ? Number(ligne.est_test) === 1 : false;
  let cle = statut, libelle = LIBELLE_STATUT[statut] || statut || 'Inconnu';
  // Un paiement "en attente" dont les 30 jours de recouvrement sont ecoules ne
  // sera JAMAIS encaisse : le dire clairement plutot que de laisser croire a une
  // attente qui se resoudra.
  const perime = ligne && !test && Number(ligne.encaisse_reel) !== 1
    && ligne.jours_restants !== null && ligne.jours_restants !== undefined
    && Number(ligne.jours_restants) < 0;
  // Un paiement de test ne doit JAMAIS porter la pastille verte "Encaissé" :
  // aucun euro n'a bouge, et c'est exactement ce qui avait fausse les totaux.
  if (test) {
    const c = COULEURS.ABANDON;
    return (
      <span title="Opération effectuée sur l’environnement de test de Monetico : aucun euro n’a été débité."
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.fond,
                     color: c.texte, borderRadius: 999, padding: '3px 10px', fontSize: 12,
                     fontWeight: 600 }}>
        <AlertTriangle size={13} /> PAIEMENT DE TEST
      </span>
    );
  }
  if (statut === 'ECHEC_TECHNIQUE') {
    const c = COULEURS.TECHNIQUE;
    return (
      <span title="Le client a voulu payer mais le formulaire a été refusé par Monetico : sa banque n'a jamais été sollicitée, et il n'a même pas pu saisir sa carte."
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.fond,
                     color: c.texte, borderRadius: 999, padding: '3px 10px', fontSize: 12,
                     fontWeight: 600 }}>
        <AlertTriangle size={13} /> PROBLÈME TECHNIQUE
      </span>
    );
  }
  const abandonne = ligne && statut === 'PENDING' && !ligne.code_retour
    && ligne.provenance === 'api-paiement'
    && (Date.now() - new Date(ligne.created_at).getTime()) > 24 * 3600 * 1000;
  if (abandonne) {
    cle = 'ABANDON'; libelle = 'NON FINALISÉ';
  } else if (statut === 'PENDING' && perime) {
    cle = 'JAMAIS'; libelle = 'JAMAIS ENCAISSÉ';
  } else if (statut === 'PAYE' && ligne && !test) {
    if (encaisse) libelle = 'ENCAISSÉ';
    else { cle = 'NON_ENCAISSE'; libelle = 'NON ENCAISSÉ'; }
  }
  const c = COULEURS[cle] || COULEURS.INCONNU;
  const Icone = c.icone;
  return (
    <span title={cle === 'NON_ENCAISSE'
      ? "Monetico a accepté l'opération mais aucun montant n'a été recouvré : rien n'est entré sur le compte."
      : cle === 'JAMAIS'
        ? "Aucun encaissement n'a eu lieu et les 30 jours de recouvrement autorisés par Monetico sont écoulés : ce montant ne rentrera plus."
        : cle === 'ABANDON'
          ? "Le client a ouvert la page de paiement mais n'a jamais validé : il n'y a jamais eu de transaction bancaire."
          : undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.fond,
                   color: c.texte, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
      <Icone size={13} /> {libelle}
    </span>
  );
}

// Regle Monetico : un paiement refuse ne peut etre recouvre que pendant 30 JOURS
// apres l'autorisation. Cette pastille dit s'il est encore temps d'agir.
function PastilleDelai({ ligne }) {
  if (!['REFUSE', 'PENDING'].includes(ligne.statut) || Number(ligne.est_test) === 1) return null;
  if (Number(ligne.encaisse_reel) === 1) return null;
  const j = ligne.jours_restants;
  if (j === null || j === undefined) return null;
  const encore = Number(j) >= 0;
  const style = {
    borderRadius: 999, padding: '2px 8px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
    background: encore ? (Number(j) <= 5 ? '#fff3e0' : '#e8f5e9') : '#eceff1',
    color: encore ? (Number(j) <= 5 ? '#e65100' : '#1b5e20') : '#607d8b',
    border: `1px solid ${encore ? (Number(j) <= 5 ? '#ffe0b2' : '#c8e6c9') : '#cfd8dc'}`
  };
  return (
    <span style={style}
          title={encore
            ? `Monetico n'autorise un recouvrement que pendant 30 jours après l'autorisation. Dernier jour possible : ${ligne.limite_recouvrement ? new Date(ligne.limite_recouvrement).toLocaleDateString('fr-FR') : '—'}.`
            : "Les 30 jours de recouvrement autorisés par Monetico sont écoulés : il faut redemander un paiement au client."}>
      {encore ? (Number(j) === 0 ? 'DERNIER JOUR' : `RECOUVRABLE ${j} J`) : 'DÉLAI 30 J DÉPASSÉ'}
    </span>
  );
}

// Petit marqueur pour les paiements qui n'existent que dans l'API de paiement
// (liens de paiement, reglements ponctuels jamais remontes dans le CRM).
function BadgeHorsCrm() {
  return (
    <span title="Paiement présent dans l'API de paiement mais pas dans les souscriptions du CRM."
          style={{ background: '#ede7f6', color: '#4527a0', border: '1px solid #d1c4e9',
                   borderRadius: 999, padding: '2px 8px', fontSize: 10.5, fontWeight: 700,
                   whiteSpace: 'nowrap' }}>
      HORS CRM
    </span>
  );
}

function Carte({ icone: Icone, libelle, valeur, couleur }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eceff1', borderTop: `3px solid ${couleur}`,
                  borderRadius: 8, padding: '14px 16px', flex: '1 1 165px',
                  boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#78909c', fontSize: 11.5,
                    textTransform: 'uppercase', letterSpacing: '.04em' }}>
        <Icone size={13} /> {libelle}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: couleur, marginTop: 5 }}>{valeur}</div>
    </div>
  );
}

/* --- fiche detaillee d'un paiement --------------------------------------- */
function Ligne({ libelle, valeur, mono }) {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #f0f2f5' }}>
      <div style={{ width: 190, flex: 'none', color: '#78909c', fontSize: 12.5 }}>{libelle}</div>
      <div style={{ fontSize: 13.5, color: '#263238', wordBreak: 'break-word',
                    fontFamily: mono ? 'ui-monospace,Menlo,Consolas,monospace' : 'inherit' }}>
        {String(valeur)}
      </div>
    </div>
  );
}


/* ---------------------------------------------------------------------------
   Traduction de la reponse technique de Monetico en langage clair.
   --------------------------------------------------------------------------- */
const ETATS_MENSUALITE = {
  PA: 'Payée — le prélèvement est passé',
  PP: 'Payée partiellement',
  RE: 'Refusée par la banque',
  AN: 'Annulée',
  AT: 'En attente de traitement'
};

const USAGE_CARTE = { debit: 'carte de débit (débit immédiat)', credit: 'carte de crédit',
                      'debit-credit': 'carte à débit différé' };
const TYPE_CARTE  = { particulier: 'carte de particulier', entreprise: "carte d'entreprise",
                      commercial: "carte d'entreprise" };

function lire3DS(valeur) {
  try {
    const texte = atob(String(valeur));
    const d = JSON.parse(texte);
    const det = d.details || {};
    const statuts = {
      authenticated: 'Authentification réussie : le client a bien validé son code ou son application bancaire.',
      attempted: "Authentification tentée : la banque n'a pas pu valider, mais l'opération a été autorisée à continuer.",
      not_authenticated: "Authentification échouée : le client n'a pas validé son identité.",
      rejected: "Authentification rejetée par la banque du client.",
      error: "Erreur technique pendant l'authentification."
    };
    return {
      protocole: `${d.protocol || '3-D Secure'} version ${d.version || '—'}`,
      statut: statuts[d.status] || d.status || '—',
      defi: det.merchantPreference === 'challenge_mandated'
        ? "Un défi a été exigé : le client devait obligatoirement confirmer dans son application bancaire."
        : null,
      responsabilite: det.liabilityShift === 'Y'
        ? "En cas de contestation, la responsabilité revient à la banque du client."
        : det.liabilityShift === 'NA'
          ? "Transfert de responsabilité non applicable ici."
          : null
    };
  } catch (e) { return null; }
}

function decrireRetour(brut, echeanceVivante) {
  if (!brut) return [];
  let d = brut;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { return []; } }
  const points = [];
  const ajout = (titre, texte) => { if (texte) points.push({ titre, texte }); };

  // Cas d'une mensualité reconstituée depuis l'état de paiement Monetico
  if (d.mensualite) {
    // l'etat interroge a l'instant fait foi ; l'instantane d'ingestion sinon
    const m = echeanceVivante || d.mensualite;
    const montant = m.montant_encaisse || m.montant;
    const encaisse = !!(montant && m.numauto);
    ajout('Nature', `Échéance mensuelle de l'abonnement ${d.base_ref || ''}.`);
    ajout('Date de l’échéance', m.date ? dateFr(m.date) : null);
    ajout('Ce que Monetico enregistre', ETATS_MENSUALITE[m.etat]
      || (m.etat ? `État « ${m.etat} » renvoyé par Monetico.` : null));
    ajout('Encaissement', encaisse
      ? `${montant} prélevés, autorisation ${m.numauto}${m.date_remise
          ? `, remise en banque le ${dateFr(m.date_remise)}` : ''}.`
      : 'Aucun prélèvement enregistré pour ce mois : Monetico n’a présenté aucun montant ' +
        'en banque pour cette échéance. Il n’y a donc ni montant ni numéro d’autorisation ' +
        'à afficher — cela ne veut pas dire que la banque a refusé.');
    if (!encaisse && /^RE$/.test(m.etat || '')) {
      ajout('Précision', 'Monetico classe cette échéance comme refusée : la présentation en banque ' +
                         'n’a pas abouti.');
    }
    if (!encaisse && /^AN$/.test(m.etat || '')) {
      ajout('Précision', 'Cette échéance a été annulée.');
    }
    if (!encaisse && /^PP$/.test(m.etat || '')) {
      ajout('Précision', 'Monetico indique un paiement partiel sans aucun recouvrement : ' +
                         'l’échéance est ouverte mais rien n’a encore été prélevé.');
    }
    return points;
  }

  // Retour complet d'un paiement
  if (d.cb_mask) {
    const details = [`Carte ${d.brand || 'bancaire'} n° ${d.cb_mask}`];
    if (USAGE_CARTE[d.cb_usage]) details.push(USAGE_CARTE[d.cb_usage]);
    if (TYPE_CARTE[d.cb_type]) details.push(TYPE_CARTE[d.cb_type]);
    if (d.cb_origin) details.push(`émise en ${d.cb_origin === 'FRA' ? 'France' : d.cb_origin}`);
    if (d.cb_ecard === 'oui') details.push('carte virtuelle à usage unique');
    ajout('Carte utilisée', details.join(', ') + '.');
  }
  ajout('Montant présenté', d.montant ? `${d.montant} ${d.devise || 'EUR'}.` : null);
  ajout("Autorisation bancaire", d.numauto
    ? `Accordée sous le numéro ${d.numauto} : le montant a bien été débité.`
    : "Aucun numéro d'autorisation n'a été délivré pour cette opération : aucun montant n'a été débité.");
  if (d.cvx) ajout('Cryptogramme', d.cvx === 'oui'
    ? 'Le cryptogramme au dos de la carte a été saisi et vérifié.'
    : 'Le cryptogramme n’a pas été vérifié.');

  const tds = d.auth_3ds ? lire3DS(d.auth_3ds) : null;
  if (tds) {
    ajout('Authentification 3-D Secure', `${tds.statut} (${tds.protocole})`);
    ajout('Niveau d’exigence', tds.defi);
    ajout('Responsabilité en cas de litige', tds.responsabilite);
  }
  if (d.client_ip) ajout('Adresse du payeur', `Paiement effectué depuis l'adresse ${d.client_ip}.`);
  if (d.motifrefus) ajout('Motif transmis par la banque', String(d.motifrefus));
  if (d.created_at) ajout('Horodatage Monetico', d.created_at + ' (heure du serveur Monetico).');

  // Synthese : le cas le plus utile a expliquer au client
  if (d.statut === 'REFUSE' && tds && /réussie/.test(tds.statut) && !d.numauto) {
    points.unshift({ titre: 'En résumé',
      texte: "Le client a bien validé son authentification bancaire, mais sa banque a ensuite refusé " +
             "l'opération. Ce refus vient donc de la banque : solde insuffisant, plafond de paiement " +
             "atteint ou opposition sur la carte. Il faut inviter le client à contacter sa banque " +
             "ou à réessayer avec une autre carte." });
  } else if (d.statut === 'REFUSE' && tds && !/réussie/.test(tds.statut)) {
    points.unshift({ titre: 'En résumé',
      texte: "Le paiement a échoué à l'étape d'authentification : le client n'a pas confirmé " +
             "l'opération dans son application bancaire, ou le délai a expiré. Il suffit de lui " +
             "renvoyer le lien de paiement et de rester joignable pendant qu'il valide." });
  }
  return points;
}

// Libelles des actions que le CRM a lui-meme declenchees chez Monetico.
const ACTIONS_CRM = {
  state: 'Lecture de l’état',
  state_auto: 'Lecture automatique (recouvrement)',
  capture: 'Mise en recouvrement',
  refund: 'Remboursement',
  'stop-recurrence': 'Arrêt de la récurrence',
  recouvrement: 'Relance de recouvrement'
};

/* Deux usages de la meme fiche :
   - depuis "Paiements en direct" : une TRANSACTION, comme chez Stripe. Uniquement
     ce paiement et son client. Ni historique, ni echeances, ni etat d'abonnement.
   - depuis "Clients et abonnements" (complet) : tout le suivi de l'abonnement. */
function FicheMonetico({ ligne, onFermer, onActualiser, complet }) {
  // Historique reel chez Monetico. Un seul appel, qui resout lui-meme la bonne
  // reference : pour une MENSUALITE, Monetico ne connait pas notre reference
  // synthetique "<ref>-M202609" (il repondait <cdr>-9</cdr> et la fiche restait
  // vide) ; il ne suit que la reference de l'ABONNEMENT, dont la reponse contient
  // l'etat de chaque echeance.
  const [hist, setHist] = useState(null);
  const [echec, setEchec] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [verif, setVerif] = useState(null);        // resultat d'une reverification a la demande
  const [enVerif, setEnVerif] = useState(false);
  const [tour, setTour] = useState(0);             // force le rechargement de l'historique

  // Interroge Monetico maintenant et remet la ligne en accord avec sa reponse.
  const reverifier = async () => {
    setEnVerif(true); setVerif(null);
    try {
      const r = await fetch(`${API}/monetico/paiement/${encodeURIComponent(ligne.reference)}/verifier`,
                            { method: 'POST', headers: jetonEntete() });
      const d = await r.json().catch(() => null);
      setVerif(r.ok ? d : { verdict: (d && d.error) || `le serveur a répondu ${r.status}` });
      if (r.ok && onActualiser) onActualiser();
      setTour((t) => t + 1);
    } catch (e) {
      setVerif({ verdict: `requête impossible (${e.message})` });
    }
    setEnVerif(false);
  };

  useEffect(() => {
    let vivant = true;
    (async () => {
      setChargement(true); setEchec(null); setHist(null);
      try {
        const r = await fetch(`${API}/monetico/paiement/${ligne.id}/historique`,
                              { headers: jetonEntete() });
        const donnees = await r.json().catch(() => null);
        if (!vivant) return;
        if (!r.ok) setEchec((donnees && donnees.error) || `le serveur a répondu ${r.status}`);
        else setHist(donnees);
      } catch (e) {
        if (vivant) setEchec(`requête impossible (${e.message})`);
      }
      if (vivant) setChargement(false);
    })();
    return () => { vivant = false; };
  }, [ligne.id, tour]);

  let brut = ligne.raw_data;
  if (typeof brut === 'string') { try { brut = JSON.parse(brut); } catch (e) { /* laisse la chaine */ } }

  return (
    <div onClick={onFermer}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,20,35,.55)', zIndex: 1000,
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 16px',
                  overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 10, width: 'min(820px,100%)',
                    boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '16px 20px', borderBottom: '1px solid #eceff1',
                      background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              {euros(ligne.montant)} — {ligne.client_nom || ligne.customer_name || ligne.company_name || 'Client inconnu'}
            </div>
            <div style={{ fontSize: 12.5, opacity: .85 }}>
              Référence {ligne.reference} · {dateHeure(ligne.created_at)}
            </div>
          </div>
          <button onClick={onFermer} aria-label="Fermer"
                  style={{ background: 'rgba(255,255,255,.18)', border: 0, color: '#fff', borderRadius: 6,
                           padding: 7, cursor: 'pointer', lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '14px 20px 22px', maxHeight: '72vh', overflowY: 'auto' }}>
          <div style={{ marginBottom: 12 }}><Etiquette statut={ligne.statut} ligne={ligne} />
            {Number(ligne.est_test) === 1 && <BadgeTest />}</div>
          <div style={{ background: Number(ligne.est_test) === 1 ? '#eceff1'
                          : (ligne.statut === 'PAYE' ? '#e8f5e9' : '#fff3e0'),
                        border: `1px solid ${Number(ligne.est_test) === 1 ? '#cfd8dc'
                          : (ligne.statut === 'PAYE' ? '#c8e6c9' : '#ffe0b2')}`,
                        borderRadius: 8, padding: '12px 14px', marginBottom: 14,
                        display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {Number(ligne.est_test) === 1
              ? <AlertTriangle size={17} color="#546e7a" style={{ flex: 'none', marginTop: 1 }} />
              : ligne.statut === 'PAYE'
                ? <CheckCircle size={17} color="#2e7d32" style={{ flex: 'none', marginTop: 1 }} />
                : <AlertTriangle size={17} color="#ef6c00" style={{ flex: 'none', marginTop: 1 }} />}
            <div style={{ fontSize: 13.5, color: '#37474f' }}>{expliquer(ligne)}</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        marginBottom: 14, fontSize: 12.5, color: '#78909c' }}>
            <span>
              {ligne.verifie_le
                ? <>État vérifié auprès de Monetico <b>{ilYA(ligne.verifie_le)}</b></>
                : <>Cet état n’a pas encore été confronté à Monetico</>}
            </span>
            <button onClick={reverifier} disabled={enVerif}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                             border: '1px solid #c5cae9', background: enVerif ? '#eceff1' : '#f0f1fb',
                             color: '#3949ab', borderRadius: 6, padding: '6px 11px',
                             fontSize: 12.5, fontWeight: 600,
                             cursor: enVerif ? 'default' : 'pointer' }}>
              <RefreshCw size={13} /> {enVerif ? 'Interrogation…' : 'Demander à Monetico maintenant'}
            </button>
            {verif && (
              <span style={{ color: verif.encaisse ? '#2e7d32' : '#e65100', fontWeight: 600 }}>
                Réponse : {verif.encaisse ? 'montant encaissé' : verif.verdict}
                {verif.corrige ? ' — la ligne a été corrigée' : ''}
              </span>
            )}
          </div>

          {/* L'ESSENTIEL, EN HAUT : l'etat reel chez Monetico et l'identite du client,
              reunis au meme endroit pour n'avoir a chercher nulle part. */}
          <div style={{ border: '1px solid #e8eaf6', borderRadius: 10, padding: '14px 16px',
                        marginBottom: 16, background: '#fcfcff' }}>
            <div style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '.05em',
                          color: '#3949ab', fontWeight: 700, marginBottom: 2 }}>
              L’état chez Monetico et le client
            </div>
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#90a4ae' }}>
              {hist && hist.interroge && hist.interroge.est_mensualite
                ? (complet
                    ? 'Cette ligne est une échéance d’abonnement. Monetico ne suit pas les échéances une par une : il répond pour l’abonnement entier. L’état de CETTE échéance est donné en premier, l’état de l’abonnement ensuite.'
                    : 'Cette ligne est une échéance d’abonnement : seul son propre état est affiché ici. Le suivi de l’abonnement et de ses autres échéances se trouve dans l’onglet « Clients et abonnements ».')
                : 'L’état réel de ce paiement interrogé chez Monetico, et les coordonnées du client telles qu’elles figurent dans le CRM et telles qu’elles ont été saisies au paiement.'}
            </p>

            {chargement ? (
              <p style={{ color: '#90a4ae', fontSize: 13, margin: '4px 0' }}>Interrogation de Monetico…</p>
            ) : hist && hist.monetico ? (
              <>
                {(() => {
                  const mens = hist.interroge && hist.interroge.est_mensualite;
                  const e = (hist.echeances || []).find((x) => x.est_cette_ligne);
                  if (!mens) return null;
                  // Cette ligne est une echeance : c'est ELLE qui compte, pas l'abonnement.
                  return (
                    <div style={{ background: e && e.encaisse ? '#e8f5e9' : '#fff3e0',
                                  border: `1px solid ${e && e.encaisse ? '#c8e6c9' : '#ffe0b2'}`,
                                  borderRadius: 8, padding: '10px 13px', marginBottom: 10 }}>
                      <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em',
                                    color: e && e.encaisse ? '#1b5e20' : '#e65100', fontWeight: 700 }}>
                        Cette échéance{e && e.date ? ` du ${dateFr(e.date)}` : ''}
                      </div>
                      <div style={{ fontSize: 13.5, color: '#37474f', marginTop: 3 }}>
                        {!e ? 'Monetico ne connaît pas encore cette échéance.'
                          : e.encaisse
                            ? `Encaissée : ${e.montant_encaisse}, autorisation ${e.numauto}${
                                e.date_remise ? `, remise en banque le ${dateFr(e.date_remise)}` : ''}.`
                            : `${e.etat_libelle || e.etat} — aucun montant n’a été prélevé pour ce mois.`}
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  const mens = hist.interroge && hist.interroge.est_mensualite;
                  // En mode transaction, on n'affiche PAS l'etat de l'abonnement :
                  // il ne concerne pas cette echeance et preterait a confusion.
                  if (mens && !complet) return null;
                  return (
                    <>
                      <Ligne libelle={mens
                               ? `État de l’abonnement ${hist.interroge.base_reference} (toutes échéances confondues)`
                               : 'État actuel chez Monetico'}
                             valeur={hist.monetico.etat_libelle || hist.monetico.etat} />
                      <Ligne libelle={mens ? 'Montant d’une échéance de l’abonnement' : 'Montant commandé'}
                             valeur={hist.monetico.montant_commande} />
                      <Ligne libelle={mens
                               ? 'Montant encaissé sur la 1re échéance de l’abonnement'
                               : 'Montant réellement encaissé'}
                             valeur={hist.monetico.montant_recouvre} />
                    </>
                  );
                })()}
                <Ligne libelle="Motif de refus (état actuel)"
                       valeur={hist.interroge && hist.interroge.est_mensualite && !complet
                         ? null : hist.monetico.motif_refus} />
                <Ligne libelle="Précision sur le refus"
                       valeur={hist.interroge && hist.interroge.est_mensualite && !complet
                         ? null : hist.monetico.motif_refus_autorisation} />
                <Ligne libelle={hist.interroge && hist.interroge.est_mensualite
                         ? 'Authentification 3-D Secure de la souscription' : 'Authentification 3-D Secure'}
                       valeur={hist.interroge && hist.interroge.est_mensualite && !complet ? null
                         : hist.monetico.status_3ds === '1'
                         ? 'Réussie — le client a bien confirmé son identité auprès de sa banque'
                         : hist.monetico.status_3ds === '0'
                           ? 'Non réussie — le client n’a pas confirmé, le refus vient de là'
                           : hist.monetico.status_3ds} />
                <Ligne libelle={hist.interroge && hist.interroge.est_mensualite && complet
                         ? 'Carte enregistrée pour l’abonnement' : 'Carte utilisée'}
                       valeur={hist.monetico.cb_masquee} mono />
                <Ligne libelle="Validité de la carte"
                       valeur={hist.monetico.validite && hist.monetico.validite.length === 4
                         ? `${hist.monetico.validite.slice(0, 2)}/${hist.monetico.validite.slice(2)}`
                         : hist.monetico.validite} />
                <Ligne libelle="Type de carte" valeur={hist.monetico.usage === 'debit' ? 'Carte de débit'
                  : hist.monetico.usage === 'credit' ? 'Carte de crédit' : hist.monetico.usage} />
                {!(hist.interroge && hist.interroge.est_mensualite && !complet) && (
                  <>
                    <Ligne libelle={hist.interroge && hist.interroge.est_mensualite
                             ? 'Numéro d’autorisation de l’abonnement' : 'Numéro d’autorisation'}
                           valeur={hist.monetico.numauto} mono />
                    <Ligne libelle={hist.interroge && hist.interroge.est_mensualite
                             ? 'Adresse IP lors de la souscription' : 'Payé depuis l’adresse IP'}
                           valeur={hist.monetico.ip_client} mono />
                  </>
                )}
              </>
            ) : (
              <p style={{ color: '#90a4ae', fontSize: 13, margin: '4px 0' }}>
                {echec ? `Monetico n’a pas pu être interrogé : ${echec}.`
                       : 'Monetico n’a renvoyé aucun état pour cette référence.'}
              </p>
            )}

            <div style={{ borderTop: '1px dashed #dfe3ea', margin: '12px 0 8px' }} />

            <Ligne libelle="Client (fiche CRM)"
                   valeur={[ligne.client_nom, ligne.client_prenom].filter(Boolean).join(' ') || null} />
            <Ligne libelle="Nom saisi au paiement" valeur={ligne.customer_name} />
            <Ligne libelle="Numéro client" valeur={ligne.client_numero} mono />
            <Ligne libelle="Email (fiche CRM)" valeur={ligne.client_email} />
            <Ligne libelle="Email (saisi au paiement)"
                   valeur={ligne.customer_email === ligne.client_email ? null : ligne.customer_email} />
            <Ligne libelle="Téléphone (fiche CRM)" valeur={ligne.client_telephone} />
            <Ligne libelle="Téléphone (saisi au paiement)"
                   valeur={ligne.customer_phone === ligne.client_telephone ? null : ligne.customer_phone} />
            <Ligne libelle="Société" valeur={ligne.client_entreprise || ligne.company_name} />
            <Ligne libelle="SIRET" valeur={ligne.client_siret || ligne.paiement_siret} mono />
            <Ligne libelle="Adresse" valeur={ligne.client_adresse || ligne.paiement_adresse} />
            <Ligne libelle="Abonnement au contrat"
                   valeur={ligne.client_prix_mensualite
                     ? `${euros(ligne.client_prix_mensualite)} par mois${ligne.client_engagement
                         ? ` — engagement de ${ligne.client_engagement} mois` : ''}` : null} />
            <Ligne libelle="Contrat signé" valeur={ligne.client_signe === null
              || ligne.client_signe === undefined ? null
              : (Number(ligne.client_signe) === 1 ? 'Oui' : 'Non')} />
            <Ligne libelle="Suivi par"
                   valeur={[ligne.client_commercial ? `${ligne.client_commercial} (commercial)` : null,
                            ligne.client_agent ? `${ligne.client_agent} (agent)` : null]
                            .filter(Boolean).join(' · ') || null} />
            <Ligne libelle="Client depuis" valeur={ligne.client_depuis ? dateFr(ligne.client_depuis) : null} />
            <Ligne libelle="Numéro de la fiche client dans le CRM" valeur={ligne.client_id} mono />
          </div>

          <h4 style={{ margin: '14px 0 4px', fontSize: 13, textTransform: 'uppercase',
                       letterSpacing: '.05em', color: '#667eea' }}>Le paiement</h4>
          <p style={{ margin: '0 0 6px', fontSize: 12, color: '#90a4ae' }}>Ce qui a été demandé, et ce que la banque a répondu.</p>
          <Ligne libelle="Montant demandé" valeur={`${euros(ligne.montant)} ${ligne.devise || ''}`} />
          <Ligne libelle="Type d’opération" valeur={ligne.mode === 'recurrent'
            ? (ligne.source === 'monetico-mensualite' ? 'Mensualité d’abonnement' : 'Abonnement récurrent')
            : 'Paiement unique'} />
          <Ligne libelle="Situation du paiement"
                 valeur={`${LIBELLE_STATUT[ligne.statut] || ligne.statut} — ${situationPrecise(ligne)}`} />
          <Ligne libelle="Motif exact renvoyé par Monetico"
                 valeur={ligne.motif_detail || ligne.motif_refus
                   ? `${motifCourt(ligne.motif_detail || ligne.motif_refus)} — Monetico écrit textuellement : « ${
                       String(ligne.motif_detail || ligne.motif_refus).replace(/^\(abonnement\)\s*/, '')} »`
                   : (Number(ligne.encaisse_reel) === 1 ? null
                      : 'Monetico n’a transmis aucun motif pour cette opération')} />
          <Ligne libelle="Causes possibles côté client"
                 valeur={Number(ligne.encaisse_reel) === 1 ? null
                   : causesPossibles(ligne.motif_detail || ligne.motif_refus || ligne.code_retour)} />
          {ligne.statut === 'REFUSE' && Number(ligne.est_test) !== 1
            && ligne.jours_restants !== null && ligne.jours_restants !== undefined && (
            <Ligne libelle="Recouvrement (règle Monetico 30 jours)"
                   valeur={Number(ligne.jours_restants) >= 0
                     ? `Possible jusqu’au ${dateFr(ligne.limite_recouvrement)} — il reste ${ligne.jours_restants} jour${Number(ligne.jours_restants) > 1 ? 's' : ''}`
                     : `Délai écoulé depuis le ${dateFr(ligne.limite_recouvrement)} : il faut redemander un paiement au client`} />
          )}
          {ligne.recouvrement_stop_reason === 'hors_delai_30j' && (
            <Ligne libelle="Relance automatique" valeur="Arrêtée : les 30 jours autorisés par Monetico sont écoulés" />
          )}
          {Number(ligne.auto_recouvrement) === 1 && !ligne.recouvrement_stop_reason
            && ligne.statut === 'REFUSE' && (
            <Ligne libelle="Relance automatique"
                   valeur={`Active — ${ligne.recouvrement_attempts || 0} tentative${Number(ligne.recouvrement_attempts) > 1 ? 's' : ''} à ce jour`} />
          )}
          <Ligne libelle="Encaissement constaté" valeur={Number(ligne.est_test) === 1
            ? 'Non — paiement de test'
            : Number(ligne.encaisse_reel) === 1
              ? 'Oui — montant recouvré par Monetico'
              : 'Non — aucun montant recouvré à ce jour'} />
          <Ligne libelle="Réponse de Monetico" valeur={traduireValeur('code_retour', ligne.code_retour)} />
          <Ligne libelle="Environnement Monetico" valeur={Number(ligne.est_test) === 1
            ? 'Environnement de TEST — aucun euro n’a été débité'
            : 'Production — les débits sont réels'} />
          <Ligne libelle="Motif de refus annoncé au paiement" valeur={ligne.motif_refus} />
          <Ligne libelle="Numéro d’autorisation" valeur={ligne.numauto} mono />
          <Ligne libelle="Moyen de paiement" valeur={traduireValeur('mode_paiement', ligne.mode_paiement)} />
          <Ligne libelle="Marque de carte" valeur={traduireValeur('brand', ligne.brand)} />
          <Ligne libelle="Terminal de paiement (TPE)"
                 valeur={ligne.tpe ? `${ligne.tpe}${String(ligne.tpe) === '7285436'
                   ? ' — terminal des abonnements' : String(ligne.tpe) === '7285286'
                   ? ' — terminal des paiements uniques' : ''}` : null} />
          <Ligne libelle="D’où vient l’enregistrement"
                 valeur={SOURCES_PAIEMENT[ligne.source] || ligne.source} />
          <Ligne libelle="Enregistré le" valeur={dateHeure(ligne.created_at)} />

          <h4 style={{ margin: '18px 0 4px', fontSize: 13, textTransform: 'uppercase',
                       letterSpacing: '.05em', color: '#667eea' }}>Le produit et son origine</h4>
          <p style={{ margin: '0 0 6px', fontSize: 12, color: '#90a4ae' }}>Ce que le client a souscrit, et par quel canal la vente est arrivée.</p>
          <Ligne libelle="Produit souscrit" valeur={ligne.produit} />
          <Ligne libelle="Identifiant technique du produit" valeur={ligne.produit_id} mono />
          <Ligne libelle="D’où vient cette vente" valeur={ligne.origine} />
          <Ligne libelle="Libellé saisi au paiement" valeur={ligne.service} />
          <Ligne libelle="Dossier" valeur={ligne.dossier} mono />
          {ligne.abonnement_reference && (
            <Ligne libelle="Abonnement d’origine"
                   valeur={`${ligne.abonnement_reference}${ligne.abonnement_depuis
                     ? ` — souscrit le ${dateFr(ligne.abonnement_depuis)}` : ''}`} />
          )}

          {brut && (() => {
            const points = decrireRetour(brut,
              hist && (hist.echeances || []).find((e) => e.est_cette_ligne));
            return (
              <>
                <h4 style={{ margin: '18px 0 8px', fontSize: 13, textTransform: 'uppercase',
                             letterSpacing: '.05em', color: '#667eea' }}>Ce que dit Monetico, en clair</h4>
          <p style={{ margin: '0 0 6px', fontSize: 12, color: '#90a4ae' }}>Traduction en français de la réponse technique de la banque.</p>
                {points.length ? points.map((p, i) => (
                  <div key={i} style={{ marginBottom: 10, padding: '10px 14px',
                                        background: p.titre === 'En résumé' ? '#f0f1fb' : '#fafbfd',
                                        border: `1px solid ${p.titre === 'En résumé' ? '#dcdff5' : '#eef1f5'}`,
                                        borderRadius: 8 }}>
                    <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em',
                                  color: p.titre === 'En résumé' ? '#667eea' : '#90a4ae',
                                  fontWeight: 700, marginBottom: 3 }}>{p.titre}</div>
                    <div style={{ fontSize: 13.5, color: '#37474f' }}>{p.texte}</div>
                  </div>
                )) : <p style={{ color: '#90a4ae', fontSize: 13 }}>
                  Monetico n'a pas transmis de détail exploitable pour cette opération.</p>}

                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: 'pointer', color: '#90a4ae', fontSize: 12.5 }}>
                    Voir la réponse technique complète</summary>
                  <pre style={{ background: '#f7f8fb', border: '1px solid #eceff1', borderRadius: 6,
                                padding: 12, fontSize: 11.5, overflowX: 'auto', marginTop: 8 }}>
{typeof brut === 'string' ? brut : JSON.stringify(brut, null, 2)}</pre>
                </details>
              </>
            );
          })()}

          {brut && typeof brut === 'object' && (
            <details style={{ marginTop: 18 }}>
              <summary style={{ cursor: 'pointer', color: '#3949ab', fontSize: 13, fontWeight: 600 }}>
                Voir TOUTES les informations de ce paiement, une par une ({
                  Object.keys(brut).filter((k) => brut[k] !== null && brut[k] !== ''
                    && typeof brut[k] !== 'object').length} informations)
              </summary>
              <p style={{ margin: '6px 0 4px', fontSize: 12, color: '#90a4ae' }}>
                Chaque donnée transmise par Monetico ou par la page de paiement, traduite en français.
              </p>
              {Object.keys(brut)
                .filter((k) => brut[k] !== null && brut[k] !== '' && typeof brut[k] !== 'object')
                .map((k) => (
                  <Ligne key={k} libelle={CHAMPS_MONETICO[k] || k}
                         valeur={traduireValeur(k, brut[k])} />
                ))}
            </details>
          )}

          {complet && (<>
          <h4 style={{ margin: '18px 0 4px', fontSize: 13, textTransform: 'uppercase',
                       letterSpacing: '.05em', color: '#667eea' }}>L’abonnement et ses échéances</h4>
          <p style={{ margin: '0 0 6px', fontSize: 12, color: '#90a4ae' }}>Interrogation en direct chez Monetico : l’état de l’abonnement et chacune de ses échéances.</p>

          {chargement ? (
            <p style={{ color: '#90a4ae', fontSize: 13 }}>Interrogation de Monetico en direct…</p>
          ) : echec ? (
            <div style={{ background: '#fdecea', border: '1px solid #f5c6c2', borderRadius: 8,
                          padding: '10px 14px', fontSize: 13, color: '#b71c1c' }}>
              Monetico n’a pas pu être interrogé : {echec}.
            </div>
          ) : !hist ? (
            <p style={{ color: '#90a4ae', fontSize: 13 }}>Aucune donnée renvoyée.</p>
          ) : (
            <>
              <div style={{ background: '#f7f8fb', border: '1px solid #eceff1', borderRadius: 8,
                            padding: '10px 13px', marginBottom: 10, fontSize: 12.5, color: '#546e7a' }}>
                {hist.interroge.est_mensualite ? (
                  <>Cette ligne est une <b>mensualité</b> d’abonnement. Monetico ne connaît pas notre
                  référence interne <code>{ligne.reference}</code> : il ne suit que l’abonnement{' '}
                  <code>{hist.interroge.base_reference}</code>. C’est donc l’abonnement qui est
                  interrogé, et l’échéance correspondant à cette ligne est surlignée ci-dessous.</>
                ) : (
                  <>Référence interrogée chez Monetico : <code>{hist.interroge.reference}</code>
                  {hist.interroge.date_commande ? <> — commande du {hist.interroge.date_commande}</> : null}
                  {hist.interroge.tpe ? <> — terminal {hist.interroge.tpe}</> : null}.</>
                )}
              </div>

              {hist.erreur && (
                <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8,
                              padding: '10px 13px', marginBottom: 10, fontSize: 13, color: '#e65100' }}>
                  {hist.erreur}
                </div>
              )}

              {hist.echeances && hist.echeances.length > 0 ? (
                <>
                  <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em',
                                color: '#90a4ae', fontWeight: 700, margin: '14px 0 5px' }}>
                    {hist.echeances.length} échéance{hist.echeances.length > 1 ? 's' : ''} connue
                    {hist.echeances.length > 1 ? 's' : ''} de Monetico pour cet abonnement
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead><tr style={{ background: '#f7f8fb' }}>
                      {['Échéance', 'Ce que dit Monetico', 'Encaissé', 'Autorisation', 'Remise en banque'].map((t) => (
                        <th key={t} style={{ textAlign: 'left', padding: '7px 9px', color: '#78909c',
                                             fontSize: 11, textTransform: 'uppercase' }}>{t}</th>))}
                    </tr></thead>
                    <tbody>
                      {hist.echeances.map((e, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f2f5',
                                             background: e.est_cette_ligne ? '#fffde7' : 'transparent' }}>
                          <td style={{ padding: '7px 9px', whiteSpace: 'nowrap' }}>
                            {e.date ? dateFr(e.date) : '—'}
                            {e.est_cette_ligne && (
                              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#f9a825',
                                             border: '1px solid #ffe082', background: '#fff8e1',
                                             borderRadius: 20, padding: '1px 7px' }}>cette ligne</span>
                            )}
                          </td>
                          <td style={{ padding: '7px 9px' }}>{e.etat_libelle || e.etat || '—'}</td>
                          <td style={{ padding: '7px 9px',
                                       color: e.encaisse ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                            {e.encaisse ? e.montant_encaisse : 'Aucun encaissement'}
                          </td>
                          <td style={{ padding: '7px 9px', fontFamily: 'ui-monospace,monospace' }}>
                            {e.numauto || '—'}</td>
                          <td style={{ padding: '7px 9px' }}>{e.date_remise ? dateFr(e.date_remise) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: 12, color: '#90a4ae', marginTop: 6 }}>
                    « Aucun encaissement » signifie que Monetico n’a enregistré aucun mouvement d’argent
                    pour cette échéance, même si son état paraît favorable.
                  </p>
                </>
              ) : (
                <p style={{ color: '#90a4ae', fontSize: 13, marginTop: 8 }}>
                  Monetico n’a renvoyé aucune échéance pour cette référence : c’est le cas des paiements
                  uniques et des abonnements dont la première échéance n’est pas encore passée en banque.
                </p>
              )}

              {hist.journal && hist.journal.length > 0 && (
                <>
                  <h4 style={{ margin: '18px 0 4px', fontSize: 13, textTransform: 'uppercase',
                               letterSpacing: '.05em', color: '#667eea' }}>
                    Actions effectuées depuis le CRM</h4>
                  <p style={{ fontSize: 12, color: '#90a4ae', margin: '0 0 6px' }}>
                    Journal interne : ce que le CRM a demandé à Monetico, automatiquement ou à la main.
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead><tr style={{ background: '#f7f8fb' }}>
                      {['Date et heure', 'Action', 'Résultat', 'Détail'].map((t) => (
                        <th key={t} style={{ textAlign: 'left', padding: '7px 9px', color: '#78909c',
                                             fontSize: 11, textTransform: 'uppercase' }}>{t}</th>))}
                    </tr></thead>
                    <tbody>
                      {hist.journal.map((o) => (
                        <tr key={o.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                          <td style={{ padding: '7px 9px', whiteSpace: 'nowrap' }}>{dateHeure(o.created_at)}</td>
                          <td style={{ padding: '7px 9px' }}>{ACTIONS_CRM[o.type] || o.type}</td>
                          <td style={{ padding: '7px 9px',
                                       color: Number(o.success) === 1 ? '#2e7d32' : '#c62828' }}>
                            {Number(o.success) === 1 ? 'Abouti' : 'Sans effet'}
                          </td>
                          <td style={{ padding: '7px 9px', color: '#546e7a' }}>
                            {o.lib || (o.etat ? (ETATS_MENSUALITE[o.etat] || o.etat)
                              : (o.cdr != null ? `code technique ${o.cdr}` : '—'))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {hist.monetico && hist.monetico.raw && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: 'pointer', color: '#90a4ae', fontSize: 12.5 }}>
                    Voir la réponse technique de Monetico</summary>
                  <pre style={{ background: '#f7f8fb', border: '1px solid #eceff1', borderRadius: 6,
                                padding: 12, fontSize: 11.5, overflowX: 'auto', marginTop: 8,
                                whiteSpace: 'pre-wrap' }}>
{hist.monetico.raw}</pre>
                </details>
              )}
            </>
          )}
          </>)}

          {!complet && (
            <p style={{ marginTop: 16, fontSize: 12.5, color: '#90a4ae' }}>
              Cette fiche ne montre que ce paiement. L’historique complet, les autres échéances
              de l’abonnement et les actions déjà menées se consultent dans l’onglet
              « Clients et abonnements ».
            </p>
          )}

        </div>
      </div>
    </div>
  );
}

/* --- pourquoi un paiement a ete refuse ------------------------------------ */
/* ─────────────────────────────────────────────────────────────────────────
   Tout ce que Monetico renvoie est en codes courts ou en anglais. Ces tables
   traduisent chaque valeur en francais comprehensible, pour qu'aucune ligne de
   l'ecran ne demande d'interpretation.
   ───────────────────────────────────────────────────────────────────────── */
const LIBELLE_STATUT = {
  PAYE: 'Encaissé',
  REFUSE: 'Refusé',
  PENDING: 'En attente',
  // Le client a voulu payer, mais notre formulaire a ete refuse par Monetico :
  // sa banque n'a jamais ete sollicitee, il n'a meme pas pu saisir sa carte.
  ECHEC_TECHNIQUE: 'Bloqué avant le paiement',
  INCONNU: 'Inconnu'
};

const CODES_RETOUR = {
  paiement:  'Paiement accepté par la banque du client',
  payetest:  'Paiement effectué sur l’environnement de TEST — aucun euro n’a été débité',
  Annulation: 'Opération annulée avant d’aboutir (page de paiement fermée, délai dépassé ou refus)',
  paiement_pf2: 'Paiement accepté (échéance d’un paiement en plusieurs fois)',
  paiement_pf3: 'Paiement accepté (échéance d’un paiement en plusieurs fois)',
  paiement_pf4: 'Paiement accepté (échéance d’un paiement en plusieurs fois)'
};

const SOURCES_PAIEMENT = {
  monetico: 'Paiement Monetico (page de paiement ou lien envoyé au client)',
  'monetico-mensualite': 'Échéance mensuelle d’un abonnement, générée par Monetico',
  'api-paiement': 'Enregistré uniquement dans l’API de paiement, pas dans le CRM'
};

const MARQUES_CARTE = {
  CB: 'Carte Bleue (CB)', VI: 'Visa', VISA: 'Visa', MC: 'Mastercard',
  MASTERCARD: 'Mastercard', AM: 'American Express', AMEX: 'American Express',
  'PAYPAL': 'PayPal'
};

const TYPES_CARTE = {
  particulier: 'Carte de particulier',
  entreprise: 'Carte d’entreprise',
  commercial: 'Carte professionnelle (commerciale)'
};

const USAGES_CARTE = { debit: 'Carte de débit', credit: 'Carte de crédit' };

const OUI_NON = { oui: 'Oui', non: 'Non', '1': 'Oui', '0': 'Non' };

/* Libelles francais de CHAQUE champ que Monetico ou l'API de paiement nous
   transmet : la section detaillee de la fiche n'affiche donc aucun nom technique. */
const CHAMPS_MONETICO = {
  reference: 'Référence de la commande',
  montant: 'Montant demandé',
  devise: 'Devise',
  statut: 'Statut enregistré',
  code_retour: 'Réponse de Monetico',
  numauto: 'Numéro d’autorisation bancaire',
  motif_refus: 'Motif de refus',
  mode_paiement: 'Moyen de paiement',
  mode: 'Type d’opération',
  brand: 'Marque de la carte',
  tpe: 'Terminal de paiement (TPE)',
  cvx: 'Cryptogramme au dos de la carte vérifié',
  cb_mask: 'Numéro de carte masqué',
  cb_bin: 'Six premiers chiffres de la carte (banque émettrice)',
  cb_hash: 'Empreinte technique de la carte (permet de reconnaître la même carte)',
  cb_validity: 'Date de validité de la carte',
  cb_origin: 'Pays d’émission de la carte',
  cb_type: 'Type de carte',
  cb_usage: 'Débit ou crédit',
  cb_ecard: 'Carte virtuelle à usage unique',
  client_ip: 'Adresse IP depuis laquelle le client a payé',
  transaction_origin: 'Pays depuis lequel le paiement a été fait',
  monetico_date: 'Date et heure enregistrées par Monetico',
  created_at: 'Enregistré le',
  updated_at: 'Mis à jour le',
  customer_name: 'Nom saisi par le client',
  customer_email: 'Email saisi par le client',
  customer_phone: 'Téléphone saisi par le client',
  company_name: 'Société',
  siret: 'SIRET',
  address: 'Adresse',
  dossier: 'Numéro de dossier',
  product_id: 'Identifiant du produit',
  product_name: 'Libellé du produit',
  texte_libre: 'Texte libre transmis au paiement',
  auth_3ds: 'Authentification 3-D Secure (détail technique, traduit plus haut)',
  base_ref: 'Référence de l’abonnement d’origine',
  mensualite: 'Détail de l’échéance'
};

/* Traduit une valeur brute en francais selon le champ concerne. */
function traduireValeur(cle, v) {
  if (v === null || v === undefined || v === '') return null;
  const t = String(v);
  if (cle === 'statut')       return LIBELLE_STATUT[t] || t;
  if (cle === 'code_retour')  return CODES_RETOUR[t] ? `${CODES_RETOUR[t]} (code « ${t} »)` : t;
  if (cle === 'brand')        return MARQUES_CARTE[t.toUpperCase()] || t;
  if (cle === 'cb_type')      return TYPES_CARTE[t.toLowerCase()] || t;
  if (cle === 'cb_usage')     return USAGES_CARTE[t.toLowerCase()] || t;
  if (cle === 'cvx' || cle === 'cb_ecard') return OUI_NON[t.toLowerCase()] || t;
  if (cle === 'mode')         return t === 'recurrent' ? 'Abonnement (prélèvement mensuel)' : 'Paiement unique';
  if (cle === 'mode_paiement')return MARQUES_CARTE[t.toUpperCase()] || (t === 'CB' ? 'Carte bancaire' : t);
  if (cle === 'cb_validity' && /^\d{4}$/.test(t)) return `${t.slice(0, 2)}/${t.slice(2)}`;
  if (cle === 'cb_origin' || cle === 'transaction_origin') return t === 'FRA' ? 'France (FRA)' : t;
  if (cle === 'devise')       return t === 'EUR' ? 'Euro (EUR)' : t;
  return t;
}

/* Motif exact du refus, en version courte, pour l'afficher a cote du statut.
   Les cles correspondent aux valeurs reellement renvoyees par Monetico dans
   motifrefus et motifrefusautorisation. */
const MOTIFS_COURTS = [
  [/3dsecure|3-d secure/i,       'authentification 3-D Secure non validée'],
  [/interdit/i,                  'carte interdite par la banque'],
  [/refus critique/i,            'refus définitif de la banque'],
  [/refus emetteur/i,            'refus de la banque émettrice'],
  [/refus/i,                     'refus de la banque du client'],
  [/filtrage|antifraude|fraude/i,'bloqué par les filtres antifraude'],
  [/depassement|plafond/i,       'plafond de la carte atteint'],
  [/solde/i,                     'compte insuffisamment approvisionné'],
  [/expir/i,                     'carte expirée'],
  [/appel/i,                     'la banque demande un contact téléphonique'],
  [/annulation/i,                'abandonné avant validation'],
  [/format|invalide/i,           'donnée invalide envoyée à Monetico'],
  [/technique/i,                 'incident technique']
];

/* Monetico ne transmet PAS la cause bancaire fine (il n'existe pas de motif
   "fonds insuffisants" dans sa reponse). Pour chaque motif qu'il renvoie, voici
   les causes possibles cote client - presentees comme telles, jamais comme un
   fait constate. */
const CAUSES_POSSIBLES = [
  [/interdit/i,        'Carte en opposition, déclarée volée ou perdue, ou bloquée par la banque.'],
  [/refus critique/i,  'Carte bloquée, compte clôturé ou interdiction bancaire : une nouvelle tentative avec la même carte est inutile.'],
  [/refus emetteur/i,  'Refus venant directement de la banque qui a émis la carte : solde insuffisant, plafond atteint, ou opération jugée anormale.'],
  [/refus/i,           'Les causes les plus fréquentes sont un solde insuffisant, un plafond de paiement atteint, ou une opposition. La banque ne précise pas laquelle.'],
  [/3dsecure/i,        'Le client n’a pas confirmé dans son application bancaire, n’a pas saisi le code reçu par SMS, ou le délai a expiré.'],
  [/annulation/i,      'Page de paiement fermée, délai dépassé, ou abandon volontaire du client.'],
  [/filtrage|fraude/i, 'Opération écartée par les filtres de sécurité (montant, pays, comportement inhabituel).']
];
const causesPossibles = (texte) => {
  if (!texte) return null;
  for (const [motif, phrase] of CAUSES_POSSIBLES) if (motif.test(String(texte))) return phrase;
  return null;
};

const motifCourt = (texte) => {
  if (!texte) return null;
  const t = String(texte);
  const abonnement = /^\(abonnement\)/.test(t);
  for (const [motif, libelle] of MOTIFS_COURTS) {
    if (motif.test(t)) return abonnement ? `${libelle} (motif de l’abonnement)` : libelle;
  }
  return t.replace(/^\(abonnement\)\s*/, '');
};

/* La vraie situation du paiement, en une ligne : l'etat ET sa cause exacte. */
/* Ce que le client etait en train de faire. Une verification de carte a
   l'ouverture d'un dossier n'est pas un "paiement unique" : c'est le geste qui
   precede l'abonnement, et l'afficher ainsi prete a confusion. */
function natureLigne(l) {
  if (l.product_id === 'es-protect-empreinte') return 'Vérification de carte';
  if (l.product_id === 'es-protect-abonnement') return 'Abonnement';
  if (l.mode === 'recurrent') return l.source === 'monetico-mensualite' ? 'Mensualité' : 'Abonnement';
  return 'Paiement unique';
}

function situationPrecise(l) {
  const test = Number(l.est_test) === 1;
  const encaisse = Number(l.encaisse_reel) === 1;
  const j = l.jours_restants;
  const perime = j !== null && j !== undefined && Number(j) < 0;
  const motif = motifCourt(l.motif_detail || l.motif_refus || l.code_retour);

  if (test) return 'environnement de test, aucun euro débité';
  if (l.statut === 'ECHEC_TECHNIQUE')
    return 'le client a essayé de payer, notre formulaire a été refusé avant la saisie de carte';
  if (encaisse) return l.numauto ? `prélèvement confirmé, autorisation ${l.numauto}`
                                 : 'prélèvement confirmé par Monetico';
  if (l.statut === 'REFUSE') return motif || 'motif non transmis par Monetico';
  if (l.statut === 'PENDING') {
    if (!l.code_retour && l.provenance === 'api-paiement'
        && (Date.now() - new Date(l.created_at).getTime()) > 24 * 3600 * 1000) {
      return 'le client n’a pas validé le paiement';
    }
    if (perime) return 'autorisé mais jamais prélevé, délai des 30 jours écoulé';
    return `pas encore prélevé${j !== null && j !== undefined
      ? ` — il reste ${j} jour${Number(j) > 1 ? 's' : ''}` : ''}`;
  }
  if (l.statut === 'PAYE' && !encaisse) return motif
    ? `accepté mais jamais prélevé — ${motif}` : 'accepté par la banque mais jamais prélevé';
  return motif;
}

const EXPLICATIONS = {
  '3DSecure': "Le client n'a pas validé l'authentification 3-D Secure : code SMS non saisi, application bancaire non confirmée, ou délai dépassé. Le débit n'a pas eu lieu.",
  'Refus': "La banque du client a refusé l'opération : solde insuffisant, plafond atteint ou opposition. Le débit n'a pas eu lieu.",
  'Interdit': "La carte est interdite d'utilisation (opposition, carte volée ou déclarée perdue). Le débit n'a pas eu lieu.",
  'Annulation': "L'opération a été annulée avant son terme, souvent parce que le client a fermé la page de paiement.",
  'filtrage': "L'opération a été bloquée par les filtres antifraude de Monetico.",
  'invalide': "Les données transmises étaient invalides ou incomplètes.",
  'Refus critique': "Refus ferme de la banque du client : carte bloquée, opposition ou compte clôturé. Inutile de réessayer, il faut un autre moyen de paiement.",
  'Depassement': "Le plafond de paiement de la carte est atteint. Le client peut réessayer plus tard ou demander un relèvement de plafond à sa banque.",
  'plafond': "Le plafond de paiement de la carte est atteint. Le client peut réessayer plus tard ou faire relever son plafond.",
  'solde': "Le compte du client n'est pas suffisamment approvisionné. Le débit n'a pas eu lieu.",
  'Appel': "La banque du client demande un contact téléphonique avant d'autoriser l'opération.",
  'suspicion': "Opération jugée suspecte par la banque ou par les filtres antifraude. Le débit n'a pas eu lieu.",
  'fraude': "Opération jugée frauduleuse par les filtres de sécurité. Le débit n'a pas eu lieu.",
  'expir': "La carte est expirée : sa date de validité est dépassée. Il faut demander une nouvelle carte au client.",
  'format': "Une donnée envoyée à Monetico n'avait pas le bon format : l'opération n'a pas pu être traitée.",
  'technique': "Incident technique chez Monetico ou chez la banque. L'opération peut être retentée."
};
function BadgeTest() {
  return <span style={{ marginLeft: 6, fontSize: 10.5, background: '#eceff1', color: '#546e7a',
                        border: '1px solid #cfd8dc', borderRadius: 999, padding: '1px 8px',
                        fontWeight: 700, letterSpacing: '.04em' }}>TEST</span>;
}

const expliquer = (l) => {
  if (l.statut === 'ECHEC_TECHNIQUE') {
    return "Le client a rempli son dossier et cliqué pour payer, mais Monetico a refusé notre "
      + "formulaire AVANT d'afficher la page de carte : il n'a jamais pu saisir ses coordonnées "
      + "bancaires et sa banque n'a jamais été sollicitée. Aucune transaction n'existe. "
      + (l.motif_refus ? `Message exact de Monetico : « ${l.motif_refus} ». ` : '')
      + "C'est un problème de notre côté, à corriger : tant qu'il dure, aucun client ne peut "
      + "aller au bout.";
  }
  if (Number(l.est_test) === 1)
    return "Paiement de TEST : opération effectuée sur l’environnement de test de Monetico. Aucun montant n’a réellement été débité.";
  if (l.statut === 'PAYE') {
    return Number(l.encaisse_reel) === 1
      ? "Paiement encaissé : Monetico a recouvré le montant, il est bien entré sur le compte."
      : "Attention : Monetico a accepté l’opération mais AUCUN montant n’a été recouvré. " +
        "Rien n’a été débité au client à ce stade — ouvrez la fiche pour voir l’état exact chez Monetico.";
  }
  if (l.statut === 'PENDING') {
    // tentative ouverte puis abandonnee : aucune transaction bancaire n'a existe
    if (!l.code_retour && l.provenance === 'api-paiement'
        && (Date.now() - new Date(l.created_at).getTime()) > 24 * 3600 * 1000) {
      return "Paiement jamais finalisé : le client a ouvert la page de paiement (ou reçu le lien) " +
             "mais n’a pas validé. Aucune transaction bancaire n’a eu lieu, il n’y a donc rien à " +
             "encaisser ni à relancer chez Monetico.";
    }
    const j = l.jours_restants;
    if (j !== null && j !== undefined && Number(j) < 0) {
      return "Ce paiement n’a JAMAIS été encaissé et ne le sera plus : Monetico avait autorisé " +
             "l’opération mais ne l’a jamais prélevée, et les 30 jours pendant lesquels un " +
             "recouvrement était possible sont écoulés. Pour récupérer cette somme, il faut " +
             "demander un nouveau paiement au client.";
    }
    return "Monetico n’a pas encore prélevé ce montant. L’état est redemandé automatiquement " +
           "toutes les 5 minutes" + (j !== null && j !== undefined
             ? `, et il reste ${j} jour${Number(j) > 1 ? 's' : ''} avant la fin du délai de recouvrement.`
             : ".");
  }
  const cle = Object.keys(EXPLICATIONS).find((k) =>
    (l.motif_refus || '').toLowerCase().includes(k.toLowerCase()) ||
    (l.code_retour || '').toLowerCase().includes(k.toLowerCase()));
  const texte = cle ? EXPLICATIONS[cle]
    : "Paiement refusé sans motif explicite. Ouvrez la fiche : le détail de la réponse de Monetico y est traduit en clair.";
  // Monetico ne laisse que 30 jours pour recouvrer : on dit s'il est encore temps.
  if (l.jours_restants !== null && l.jours_restants !== undefined) {
    return Number(l.jours_restants) >= 0
      ? `${texte} Il reste ${l.jours_restants} jour${Number(l.jours_restants) > 1 ? 's' : ''} pour le recouvrer (limite Monetico de 30 jours).`
      : `${texte} Les 30 jours de recouvrement autorisés par Monetico sont écoulés : il faut redemander un paiement au client.`;
  }
  return texte;
};

/* --- vue 1 : les clients et leurs paiements regroupes --------------------- */
function VueClients({ lignesToutes, onOuvrirFiche }) {
  const [clients, setClients] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(null);   // cle du client deplie

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/monetico/clients`, { headers: jetonEntete() });
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        setClients(await r.json());
      } catch (e) { setErreur(e.message); setClients([]); }
      setChargement(false);
    })();
  }, []);

  const liste = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => [c.nom, c.email, c.societe, c.dossier,
      ...(c.produits || [])].some((v) => (v || '').toLowerCase().includes(q)));
  }, [clients, recherche]);

  if (chargement) return <p style={{ color: '#90a4ae' }}>Chargement…</p>;
  if (erreur) return <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px',
                                   borderRadius: 6 }}>Impossible de charger les clients : {erreur}</div>;

  const paiementsDe = (c) => lignesToutes.filter((l) =>
    (c.client_id && String(l.client_id) === String(c.client_id)) ||
    (c.email && [l.customer_email, l.client_email].includes(c.email)) ||
    (c.nom && [l.customer_name, l.client_nom].includes(c.nom)));

  return (
    <>
      <div style={{ position: 'relative', maxWidth: 380, marginBottom: 14 }}>
        <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#b0bec5' }} />
        <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
               placeholder="Rechercher un client…"
               style={{ width: '100%', padding: '9px 12px 9px 34px', border: '1px solid #dfe3ea',
                        borderRadius: 6, fontSize: 13.5 }} />
      </div>
      <p style={{ color: '#78909c', fontSize: 12.5, marginBottom: 8 }}>
        {liste.length} client{liste.length > 1 ? 's' : ''} ayant au moins un paiement
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {liste.map((c) => (
          <div key={c.cle} style={{ border: '1px solid #eceff1', borderRadius: 8, background: '#fff' }}>
          <div onClick={() => setOuvert(ouvert === c.cle ? null : c.cle)}
               style={{ padding: '14px 16px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 14 }}
               onMouseEnter={(e) => { e.currentTarget.style.background = '#f7f8fb'; }}
               onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: '#263238' }}>
                {c.nom || '—'}
                {c.abonne === 1 && <span style={{ marginLeft: 8, fontSize: 11, background: '#ede7f6',
                  color: '#4527a0', borderRadius: 999, padding: '2px 9px' }}>Abonné</span>}
                {Number(c.tests) > 0 && <span style={{ marginLeft: 6, fontSize: 11, background: '#eceff1',
                  color: '#546e7a', borderRadius: 999, padding: '2px 9px' }}>
                  {c.tests} paiement{Number(c.tests) > 1 ? 's' : ''} de test</span>}
                {c.relance_en_cours === 1 && <span style={{ marginLeft: 6, fontSize: 11, background: '#fff3e0',
                  color: '#e65100', borderRadius: 999, padding: '2px 9px' }}>Relance en cours</span>}
              </div>
              <div style={{ color: '#90a4ae', fontSize: 12 }}>{c.email || ''}{c.societe ? ` · ${c.societe}` : ''}</div>
              <div style={{ color: '#607d8b', fontSize: 11.5, marginTop: 2 }}>
                {(c.produits && c.produits.length ? c.produits.join(' · ') : 'Produit non renseigné')}
                {c.abonne_depuis ? ` — abonné depuis le ${dateFr(c.abonne_depuis)}` : ''}
                {c.dossier ? ` — dossier ${c.dossier}` : ''}
              </div>
              {c.origines && c.origines.length > 0 && (
                <div style={{ color: '#b0bec5', fontSize: 11, marginTop: 1 }}>{c.origines.join(' · ')}</div>
              )}
            </div>
            <div style={{ textAlign: 'right', flex: 'none' }}>
              <div style={{ fontWeight: 700, color: '#2e7d32' }}>{euros(c.total_encaisse)}</div>
              <div style={{ color: '#90a4ae', fontSize: 11.5 }}>{c.operations} opération{c.operations > 1 ? 's' : ''}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
              <span title="Payés" style={{ background: '#e8f5e9', color: '#1b5e20', borderRadius: 6,
                padding: '3px 9px', fontSize: 12, fontWeight: 600 }}>{c.payes}</span>
              <span title="Refusés" style={{ background: '#ffebee', color: '#b71c1c', borderRadius: 6,
                padding: '3px 9px', fontSize: 12, fontWeight: 600 }}>{c.refuses}</span>
              <span title="En attente" style={{ background: '#fff8e1', color: '#8d6e00', borderRadius: 6,
                padding: '3px 9px', fontSize: 12, fontWeight: 600 }}>{c.en_attente}</span>
            </div>
            <div style={{ flex: 'none', textAlign: 'right', minWidth: 120 }}>
              <div style={{ color: '#90a4ae', fontSize: 11.5 }}>Dernier paiement</div>
              <div style={{ color: '#546e7a', fontSize: 12.5 }}>{dateFr(c.dernier_paiement)}</div>
            </div>
            <ChevronRight size={18} color="#b0bec5" style={{ flex: 'none',
              transform: ouvert === c.cle ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }} />
          </div>

          {ouvert === c.cle && (
            <div style={{ borderTop: '1px solid #eceff1', padding: '10px 16px 14px', background: '#fbfcfe' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>
                  {['Date et heure', 'Montant', 'Nature', 'Situation', 'Ce qui s’est passé'].map((t) => (
                    <th key={t} style={{ textAlign: 'left', padding: '6px 8px', color: '#90a4ae',
                                         fontSize: 10.5, textTransform: 'uppercase' }}>{t}</th>))}
                </tr></thead>
                <tbody>
                  {paiementsDe(c).map((l) => (
                    <tr key={l.id} onClick={() => onOuvrirFiche(l)}
                        style={{ cursor: 'pointer', borderTop: '1px solid #eef1f5' }}>
                      <td style={{ padding: '7px 8px', whiteSpace: 'nowrap', color: '#546e7a' }}>
                        {dateFr(l.created_at)} <span style={{ color: '#b0bec5' }}>{heureFr(l.created_at)}</span></td>
                      <td style={{ padding: '7px 8px', fontWeight: 600 }}>{euros(l.montant)}</td>
                      <td style={{ padding: '7px 8px', color: '#546e7a' }}>
                        {natureLigne(l)}
                        {l.product_id === 'es-protect-empreinte' && (
                          <div style={{ color: '#90a4ae', fontSize: 11.5 }}>
                            avant abonnement 89 €/mois</div>
                        )}</td>
                      <td style={{ padding: '7px 8px' }}>
                        <Etiquette statut={l.statut} ligne={l} />
                        {Number(l.est_test) === 1 && <BadgeTest />}
                        <div style={{ marginTop: 3, fontSize: 11,
                                      color: Number(l.encaisse_reel) === 1 ? '#2e7d32' : '#e65100' }}>
                          {situationPrecise(l)}
                        </div></td>
                      <td style={{ padding: '7px 8px', color: '#78909c', fontSize: 12 }}>{expliquer(l)}</td>
                    </tr>
                  ))}
                  {!paiementsDe(c).length && (
                    <tr><td colSpan={5} style={{ padding: 12, color: '#b0bec5' }}>
                      Aucun paiement chargé pour ce client.</td></tr>)}
                </tbody>
              </table>
            </div>
          )}
          </div>
        ))}
        {!liste.length && <p style={{ color: '#90a4ae' }}>Aucun client ne correspond.</p>}
      </div>
    </>
  );
}

/* --- ecran principal ------------------------------------------------------ */
export default function ClientsMonetico() {
  const [donnees, setDonnees] = useState({ stats: {}, lignes: [], mois: [] });
  const [majLe, setMajLe] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [statut, setStatut] = useState('');
  const [mode, setMode] = useState('');
  const [recherche, setRecherche] = useState('');
  const [selection, setSelection] = useState(null);
  const [vue, setVue] = useState('paiements');     // 'paiements' | 'clients'
  const [encaissesSeuls, setEncaissesSeuls] = useState(false);
  const [periode, setPeriode] = useState('');      // preselection
  const [du, setDu] = useState('');                // bornes saisies a la main
  const [au, setAu] = useState('');

  /* silencieux = rafraichissement automatique : on remplace les donnees sans
     retirer le tableau de la page, sinon tout saute sous les yeux du lecteur */
  const charger = async ({ silencieux = false } = {}) => {
    if (!silencieux) setChargement(true);
    setErreur(null);
    try {
      const params = new URLSearchParams();
      if (statut) params.set('statut', statut);
      if (mode) params.set('mode', mode);
      if (du) params.set('depuis', `${du}T00:00:00Z`);
      if (au) params.set('jusqua', `${au}T23:59:59Z`);
      if (!du && !au && periode) {
        const [debut, fin] = bornesPeriode(periode);
        if (debut) params.set('depuis', debut);
        if (fin) params.set('jusqua', fin);
      }
      const r = await fetch(`${API}/monetico/synthese?${params}`, { headers: jetonEntete() });
      if (!r.ok) throw new Error(`Erreur ${r.status}`);
      setDonnees(await r.json());
      setMajLe(new Date().toISOString());   // horodatage de la derniere mise a jour
    } catch (e) { setErreur(e.message); }
    if (!silencieux) setChargement(false);
  };

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [statut, mode, periode, du, au]);

  // Mise a jour automatique toutes les 30 secondes, en place (ni perte de filtre,
  // ni defilement qui saute). Suspendue pendant la lecture d'une fiche.
  useRafraichissementAuto(() => charger({ silencieux: true }), { secondes: 30, actif: !selection });
  useBlocageDefilement(Boolean(selection));
  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape') setSelection(null); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, []);

  const lignes = useMemo(() => {
    const base = encaissesSeuls
      ? donnees.lignes.filter((l) => Number(l.encaisse_reel) === 1)
      : donnees.lignes;
    const q = recherche.trim().toLowerCase();
    if (!q) return base;
    return base.filter((l) => [l.client_nom, l.customer_name, l.customer_email,
      l.client_email, l.company_name, l.reference, l.service, l.produit, l.produit_id,
      l.dossier, l.abonnement_reference]
      .some((v) => (v || '').toLowerCase().includes(q)));
  }, [donnees.lignes, recherche, encaissesSeuls]);

  const s = donnees.stats || {};
  const champ = { padding: '9px 12px', border: '1px solid #dfe3ea', borderRadius: 6, fontSize: 13.5, background: '#fff' };

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '22px 24px 28px',
                  boxShadow: '0 4px 18px rgba(0,0,0,.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 9, margin: 0, fontSize: 20, color: '#263238' }}>
          <CreditCard size={20} color="#667eea" /> Clients Monetico
        </h2>
        <details style={{ flex: '1 1 100%', order: 9 }}>
          <summary style={{ cursor: 'pointer', color: '#3949ab', fontSize: 12.5 }}>
            Comment lire cet écran ?
          </summary>
          <div style={{ background: '#f7f8fb', border: '1px solid #eceff1', borderRadius: 8,
                        padding: '12px 15px', marginTop: 8, fontSize: 13, color: '#455a64',
                        lineHeight: 1.7 }}>
            <div><b>Encaissé</b> — Monetico a bien prélevé le montant, l’argent est sur le compte.</div>
            <div><b style={{ color: '#e65100' }}>Non encaissé</b> — la banque a accepté l’opération
              mais aucun montant n’a été prélevé : rien n’est arrivé sur le compte.</div>
            <div><b style={{ color: '#b71c1c' }}>Refusé</b> — la banque a refusé. La colonne
              « Ce qui s’est passé » dit pourquoi, en français.</div>
            <div><b style={{ color: '#8d6e00' }}>En attente</b> — Monetico n’a pas encore prélevé.
              L’état est redemandé automatiquement toutes les 5 minutes, et le délai de
              recouvrement court toujours.</div>
            <div><b style={{ color: '#78909c' }}>Non finalisé</b> — le client a ouvert la page de
              paiement sans valider : aucune transaction bancaire n’a existé. Ce sont des paniers
              abandonnés, pas des impayés.</div>
            <div><b style={{ color: '#b71c1c' }}>Jamais encaissé</b> — l’opération avait été
              autorisée mais jamais prélevée, et les 30 jours sont écoulés : cette somme ne
              rentrera plus, il faut redemander un paiement au client.</div>
            <div><b style={{ color: '#546e7a' }}>TEST</b> — essai sur l’environnement de test :
              aucun euro n’a bougé, et ces lignes ne comptent dans aucun total.</div>
            <div><b style={{ color: '#4527a0' }}>HORS CRM</b> — paiement présent dans l’API de
              paiement mais pas dans les souscriptions du CRM.</div>
            <div><b style={{ color: '#1b5e20' }}>Recouvrable X j</b> — un paiement refusé ne peut être
              récupéré que pendant 30 jours après l’autorisation : c’est le temps qu’il vous reste.</div>
            <div style={{ marginTop: 6 }}>
              Sous chaque pastille, la <b>cause exacte</b> est écrite : refus de la banque,
              carte interdite, 3-D Secure non validée, plafond atteint, client qui n’a pas validé…
              Quand Monetico ne transmet pas de motif pour une échéance, c’est dit tel quel.
            </div>
            <div style={{ marginTop: 6, color: '#78909c' }}>
              Un clic sur une ligne ouvre sa fiche : montant, carte, motif exact du refus,
              produit souscrit, origine de la vente et historique complet chez Monetico.
            </div>
          </div>
        </details>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                         color: '#2e7d32', background: '#f1f8f2', border: '1px solid #c8e6c9',
                         borderRadius: 999, padding: '4px 11px' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2e7d32',
                           display: 'inline-block' }} />
            Mise à jour automatique{majLe ? ` · dernière à ${heureFr(majLe)}` : ''}
          </span>
          <button onClick={() => charger({ silencieux: true })}
                  style={{ ...champ, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <RefreshCw size={14} /> Actualiser maintenant
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '2px solid #eceff1' }}>
        {[['paiements', 'Paiements en direct', Activity], ['clients', 'Clients et abonnements', Users]]
          .map(([cle, libelle, Icone]) => (
          <button key={cle} onClick={() => setVue(cle)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, border: 0, background: 'none',
                           padding: '10px 16px', cursor: 'pointer', fontSize: 14,
                           fontWeight: vue === cle ? 700 : 500,
                           color: vue === cle ? '#667eea' : '#78909c',
                           borderBottom: vue === cle ? '2px solid #667eea' : '2px solid transparent',
                           marginBottom: -2 }}>
            <Icone size={15} /> {libelle}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <Carte icone={Coins} libelle="Réellement encaissé" valeur={euros(s.encaisse)} couleur="#2e7d32" />
        <Carte icone={RefreshCw} libelle="Revenu mensuel" valeur={euros(s.revenu_mensuel)} couleur="#667eea" />
        <Carte icone={Users} libelle="Abonnés" valeur={s.abonnes || 0} couleur="#764ba2" />
        <Carte icone={CheckCircle} libelle="Encaissements confirmés" valeur={s.payes || 0} couleur="#2e7d32" />
        <Carte icone={XCircle} libelle="Refusés" valeur={s.refuses || 0} couleur="#c62828" />
        <Carte icone={Clock} libelle="En attente" valeur={s.en_attente || 0} couleur="#ef6c00" />
        <Carte icone={AlertTriangle} libelle="Paiements de test" valeur={s.tests || 0} couleur="#78909c" />
        {Number(s.bloques_technique) > 0 && (
          <Carte icone={AlertTriangle} libelle="Bloqués avant le paiement"
                 valeur={s.bloques_technique} couleur="#6b21a8" />
        )}
      </div>

      {Number(s.annonces_non_encaisses) > 0 && (
        <div style={{ background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: 8,
                      padding: '11px 14px', marginBottom: 16, fontSize: 13, color: '#e65100',
                      display: 'flex', gap: 9, alignItems: 'flex-start' }}>
          <AlertTriangle size={17} style={{ flex: 'none', marginTop: 1 }} />
          <div><b>{s.annonces_non_encaisses} paiement{s.annonces_non_encaisses > 1 ? 's' : ''}</b> affiché
          {s.annonces_non_encaisses > 1 ? 's' : ''} comme accepté{s.annonces_non_encaisses > 1 ? 's' : ''} par
          Monetico alors qu’aucun montant n’a été recouvré. Ces montants ne sont pas comptés dans l’encaissé.</div>
        </div>
      )}

      {vue === 'clients' ? (
        <VueClients lignesToutes={donnees.lignes} onOuvrirFiche={(l) => setSelection(l)} />
      ) : (
      <>
      <PanneauParcours titre="Ce qui se passe en ce moment sur ES-PROTECT" />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#b0bec5' }} />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
                 placeholder="Rechercher un client, un email, une référence…"
                 style={{ ...champ, width: '100%', paddingLeft: 34 }} />
        </div>
        <select value={statut} onChange={(e) => setStatut(e.target.value)} style={champ}>
          <option value="">Tous les statuts</option>
          <option value="PAYE">Encaissés</option>
          <option value="REFUSE">Refusés</option>
          <option value="PENDING">En attente</option>
          <option value="ECHEC_TECHNIQUE">Bloqués par un problème technique</option>
        </select>
        <select value={mode} onChange={(e) => setMode(e.target.value)} style={champ}>
          <option value="">Tous les types</option>
          <option value="recurrent">Abonnements</option>
          <option value="immediat">Paiements uniques</option>
        </select>
        <label style={{ ...champ, display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                        color: encaissesSeuls ? '#2e7d32' : '#546e7a',
                        borderColor: encaissesSeuls ? '#a5d6a7' : '#dfe3ea',
                        background: encaissesSeuls ? '#f1f8f2' : '#fff', fontWeight: encaissesSeuls ? 700 : 500 }}>
          <input type="checkbox" checked={encaissesSeuls}
                 onChange={(e) => setEncaissesSeuls(e.target.checked)} />
          Encaissements réels uniquement
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {[['', 'Tout'], ['aujourdhui', "Aujourd'hui"], ['semaine', '7 jours'],
          ['mois', 'Ce mois-ci'], ['mois_precedent', 'Mois précédent'], ['annee', 'Cette année']]
          .map(([cle, libelle]) => (
          <button key={cle} onClick={() => { setPeriode(cle); setDu(''); setAu(''); }}
                  style={{ border: `1px solid ${periode === cle && !du && !au ? '#667eea' : '#dfe3ea'}`,
                           background: periode === cle && !du && !au ? '#f0f1fb' : '#fff',
                           color: periode === cle && !du && !au ? '#667eea' : '#546e7a',
                           borderRadius: 999, padding: '6px 14px', cursor: 'pointer',
                           fontSize: 12.5, fontWeight: periode === cle && !du && !au ? 700 : 500 }}>
            {libelle}
          </button>
        ))}
        <span style={{ color: '#b0bec5', fontSize: 12.5, marginLeft: 4 }}>ou</span>
        <label style={{ fontSize: 12.5, color: '#78909c' }}>du</label>
        <input type="date" value={du} onChange={(e) => { setDu(e.target.value); setPeriode(''); }}
               style={{ ...champ, padding: '7px 10px' }} />
        <label style={{ fontSize: 12.5, color: '#78909c' }}>au</label>
        <input type="date" value={au} onChange={(e) => { setAu(e.target.value); setPeriode(''); }}
               style={{ ...champ, padding: '7px 10px' }} />
        {(du || au) && (
          <button onClick={() => { setDu(''); setAu(''); }}
                  style={{ ...champ, padding: '6px 12px', cursor: 'pointer', fontSize: 12.5 }}>
            Effacer les dates
          </button>
        )}
      </div>

      {erreur && <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px',
                               borderRadius: 6, marginBottom: 14 }}>Erreur : {erreur}</div>}

      {chargement ? <p style={{ color: '#90a4ae' }}>Chargement…</p> : (
        <>
          <p style={{ color: '#78909c', fontSize: 12.5, marginBottom: 8 }}>
            {lignes.length} ligne{lignes.length > 1 ? 's' : ''} — cliquez sur une ligne pour voir le détail
            {Number(s.hors_crm) > 0 && !encaissesSeuls && (
              <> · dont {s.hors_crm} paiement{s.hors_crm > 1 ? 's' : ''} présent
              {s.hors_crm > 1 ? 's' : ''} uniquement dans l’API de paiement (marqué
              {s.hors_crm > 1 ? 's' : ''} « hors CRM »)</>
            )}
          </p>
          <div style={{ border: '1px solid #eceff1', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ maxHeight: '58vh', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, background: '#fff' }}>
                <thead>
                  <tr>
                    {['Date et heure', 'Client', 'Montant', 'Nature', 'Situation', 'Ce qui s’est passé', 'Référence'].map((t) => (
                      <th key={t} style={{ textAlign: 'left', padding: '11px 12px', background: '#f7f8fb',
                                           borderBottom: '1px solid #eceff1', position: 'sticky', top: 0,
                                           fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em',
                                           color: '#78909c', zIndex: 1 }}>{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => (
                    <tr key={l.id} onClick={() => setSelection(l)}
                        style={{ cursor: 'pointer', background: i % 2 ? '#fcfcfd' : '#fff',
                                 borderBottom: '1px solid #f3f5f8' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f1fb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 ? '#fcfcfd' : '#fff'; }}>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#546e7a' }}>
                        {dateFr(l.created_at)}
                        <div style={{ color: '#90a4ae', fontSize: 11.5 }}>{heureFr(l.created_at)}</div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 600, color: '#263238' }}>
                          {l.client_nom || l.customer_name || l.company_name || '—'}</div>
                        <div style={{ color: '#90a4ae', fontSize: 11.5 }}>
                          {l.client_email || l.customer_email || ''}</div>
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, whiteSpace: 'nowrap', color: '#263238' }}>
                        {euros(l.montant)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#546e7a' }}>
                        {l.mode === 'recurrent'
                          ? (l.source === 'monetico-mensualite' ? 'Mensualité' : 'Abonnement')
                          : 'Paiement unique'}</td>
                      <td style={{ padding: '10px 12px', width: 250, minWidth: 210, maxWidth: 250,
                                   verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          <Etiquette statut={l.statut} ligne={l} />
                          {Number(l.est_test) === 1 && <BadgeTest />}
                          {l.provenance === 'api-paiement' && <BadgeHorsCrm />}
                          <PastilleDelai ligne={l} />
                        </div>
                        <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.4,
                                      whiteSpace: 'normal', wordBreak: 'break-word',
                                      color: Number(l.encaisse_reel) === 1 ? '#2e7d32' : '#e65100' }}>
                          {situationPrecise(l)}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: 330, color: '#546e7a', fontSize: 12.5 }}>
                        {expliquer(l)}
                        <div style={{ color: '#b0bec5', fontSize: 11, marginTop: 2 }}>
                          {[l.brand, l.mode_paiement, l.motif_refus].filter(Boolean).join(' · ')}</div></td>
                      <td style={{ padding: '10px 12px', fontFamily: 'ui-monospace,Menlo,monospace',
                                   fontSize: 11.5, color: '#90a4ae' }}>{l.reference}</td>
                    </tr>
                  ))}
                  {!lignes.length && (
                    <tr><td colSpan={7} style={{ padding: 26, textAlign: 'center', color: '#90a4ae' }}>
                      Aucun paiement ne correspond à ces critères.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {donnees.mois && donnees.mois.length > 0 && (
            <>
              <h3 style={{ marginTop: 26, marginBottom: 4, fontSize: 14.5, color: '#263238' }}>Mois par mois</h3>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#90a4ae' }}>
                Montants réellement encaissés, classés selon le mois de l’opération.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {donnees.mois.map((m) => (
                  <div key={m.mois} style={{ border: '1px solid #eceff1', borderRadius: 8,
                                             padding: '10px 14px', background: '#fff', minWidth: 132 }}>
                    <div style={{ color: '#546e7a', fontSize: 12.5, fontWeight: 600,
                                  textTransform: 'capitalize' }}>{m.libelle || m.mois}</div>
                    <div style={{ fontWeight: 700, color: m.encaisse > 0 ? '#2e7d32' : '#b0bec5',
                                  fontSize: 17, marginTop: 3 }}>{euros(m.encaisse)}</div>
                    <div style={{ color: '#90a4ae', fontSize: 11 }}>réellement encaissé</div>
                    <div style={{ color: '#b0bec5', fontSize: 11, marginTop: 4 }}>
                      {m.encaissees || 0} encaissement{(m.encaissees || 0) > 1 ? 's' : ''} sur {m.nombre} opération{m.nombre > 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      </>
      )}

      {selection && <FicheMonetico ligne={selection} onFermer={() => setSelection(null)}
                                   onActualiser={charger} complet={vue === 'clients'} />}
    </div>
  );
}
